import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../const/routes';
import axiosInstance from '../../services/AxiosInstance';
import socket from '../../utils/socketClient';
import { fetchUserData } from '../../utils/api';
import { FaPencilAlt } from 'react-icons/fa';
import BotonChico03 from '../Botones/BotonChico03/BotonChico03';
import { useUser } from '../../context/UserContext';
import {
  AVATAR_POR_DEFECTO,
  AVATARES_DISPONIBLES,
  getAvatarSrc,
  handleAvatarError,
} from '../../utils/avatar';
import { useTranslation } from 'react-i18next';

const FIELD_MAX_LENGTHS = {
  nick_usuario: 15,
  nombre_usuario: 20,
  apellido_usuario: 20,
};

const FIELD_INPUT_TYPES = {
  contrasenia: 'password',
  fecha_nacimiento: 'date',
  email: 'email',
};

const LOCKED_AVATARS = {
  'avatar_901.png': {
    saldoMinimo: 99999999,
  },
};

const CardPerfil = () => {
  const navigate = useNavigate();
  const { silentRefreshUserData } = useUser();
  const { t } = useTranslation();

  const FIELD_LABELS = {
    nombre_usuario: t('cardPerfil.nameField'),
    apellido_usuario: t('cardPerfil.lastNameField'),
    nick_usuario: t('cardPerfil.nickField'),
    email: t('cardPerfil.emailField'),
    fecha_nacimiento: t('cardPerfil.birthdateField'),
    contrasenia: t('cardPerfil.passwordField'),
  };

  const PROFILE_FIELDS = [
    { key: 'nombre_usuario', label: t('cardPerfil.nameField') },
    { key: 'apellido_usuario', label: t('cardPerfil.lastNameField') },
    { key: 'nick_usuario', label: t('cardPerfil.nickField') },
    { key: 'email', label: t('cardPerfil.emailField') },
    { key: 'fecha_nacimiento', label: t('cardPerfil.birthdateField') },
    { key: 'contrasenia', label: t('cardPerfil.passwordField'), displayValue: '********' },
  ];

  const [userData, setUserData] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    nick_usuario: '',
    nombre_usuario: '',
    apellido_usuario: '',
    fecha_nacimiento: '',
    contrasenia: '',
  });

  const [isEditing, setIsEditing] = useState(false);
  const [currentField, setCurrentField] = useState('');
  const [currentValue, setCurrentValue] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    contrasenia_actual: '',
    nueva_contrasenia: '',
    confirmar_nueva_contrasenia: '',
  });
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const avatarModalRef = useRef(null);
  const [tooltipAvatar, setTooltipAvatar] = useState(null);

  const lockedSet = useMemo(() => {
    if (!userData) return new Set();
    const set = new Set();
    Object.entries(LOCKED_AVATARS).forEach(([name, { saldoMinimo }]) => {
      if ((userData.saldo ?? 0) < saldoMinimo) set.add(name);
    });
    return set;
  }, [userData]);

  useEffect(() => {
    if (!isAvatarModalOpen) return;
    function handleClickOutside(event) {
      if (
        avatarModalRef.current &&
        !avatarModalRef.current.contains(event.target)
      ) {
        setIsAvatarModalOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAvatarModalOpen]);

  useEffect(() => {
    fetchUserData(navigate, setUserData);
  }, [navigate]);

  useEffect(() => {
    if (userData) {
      setFormData({
        nick_usuario: userData.nick_usuario || '',
        nombre_usuario: userData.nombre_usuario || '',
        apellido_usuario: userData.apellido_usuario || '',
        fecha_nacimiento: userData.fecha_nacimiento.split('T')[0] || '',
        contrasenia: '',
        email: userData.email || '',
      });
      setSelectedAvatar(userData.avatar);
    }
  }, [userData]);

  useEffect(() => {
    if (isAvatarModalOpen || isEditing || showResetConfirm) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => document.body.classList.remove('overflow-hidden');
  }, [isAvatarModalOpen, isEditing, showResetConfirm]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(false);
        setError(null);
      }, 10000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleEditClick = (field) => {
    setCurrentField(field);
    setCurrentValue(formData[field]);
    if (field === 'contrasenia') {
      setPasswordForm({
        contrasenia_actual: '',
        nueva_contrasenia: '',
        confirmar_nueva_contrasenia: '',
      });
    }
    setError(null);
    setSuccess(false);
    setIsEditing(true);
  };

  const updateUser = async (updateData) => {
    setError(null);
    setSuccess(false);

    try {
      const response = await axiosInstance.patch(
        '/api/usuario/updateUsuario',
        updateData
      );

      setUserData((prev) => ({ ...prev, ...response.data }));
      setSuccess(true);
      socket.emit('actualizacion-usuario', response.data);
      return true;
    } catch (err) {
      if (err.response?.data?.errors) {
        const errorMessages = err.response.data.errors
          .map((e) => e.msg)
          .join(' | ');
        setError(errorMessages);
      } else if (err.response?.data?.msg) {
        setError(err.response.data.msg);
      } else {
        setError(t('cardPerfil.updateError'));
      }
      return false;
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    if (currentField === 'contrasenia') {
      const {
        contrasenia_actual,
        nueva_contrasenia,
        confirmar_nueva_contrasenia,
      } = passwordForm;

      if (
        !contrasenia_actual.trim() ||
        !nueva_contrasenia.trim() ||
        !confirmar_nueva_contrasenia.trim()
      ) {
        setError(t('cardPerfil.passwordFieldsRequired'));
        return;
      }

      if (nueva_contrasenia !== confirmar_nueva_contrasenia) {
        setError(t('cardPerfil.passwordMismatch'));
        return;
      }

      const updated = await updateUser({
        contrasenia_actual,
        nueva_contrasenia,
        confirmar_nueva_contrasenia,
      });

      if (updated) {
        setIsEditing(false);
        setCurrentField('');
        setCurrentValue('');
        setPasswordForm({
          contrasenia_actual: '',
          nueva_contrasenia: '',
          confirmar_nueva_contrasenia: '',
        });
      }
      return;
    }

    const updated = await updateUser({ [currentField]: currentValue });
    if (updated) {
      setFormData((prev) => ({ ...prev, [currentField]: currentValue }));
      setIsEditing(false);
      setCurrentField('');
      setCurrentValue('');
    }
  };

  const handleAvatarUpdate = async () => {
    const updated = await updateUser({ avatar: selectedAvatar });
    if (updated) {
      setIsAvatarModalOpen(false);
    }
  };

  const handleEditCancel = () => {
    setError(null);
    setPasswordForm({
      contrasenia_actual: '',
      nueva_contrasenia: '',
      confirmar_nueva_contrasenia: '',
    });
    setIsEditing(false);
  };

  const handleAvatarCancel = () => {
    setSelectedAvatar(userData?.avatar);
    setIsAvatarModalOpen(false);
  };

  const handleResetSaldo = async () => {
    setShowResetConfirm(false);
    setResetting(true);
    setError(null);
    try {
      const response = await axiosInstance.post('/api/usuario/resetSaldo');
      setUserData((prev) => ({
        ...prev,
        saldo: response.data.saldo,
        reset: response.data.reset,
      }));
      setSuccess(true);
      silentRefreshUserData();
      socket.emit('actualizacion-usuario', response.data);
    } catch (err) {
      setError(err.response?.data?.msg || t('cardPerfil.resetError'));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div>
      {userData && (
        <div className="rounded-lg overflow-hidden border-4 border-slate-200 bg-slate-950 p-4 sm:p-6 shadow-violet-500/50 shadow-[0_5px_15px_0_rgba(148,0,211,0.5)]">
          <h1 className="text-2xl font-bold text-white mb-5 text-center">
            {t('cardPerfil.title')}
          </h1>

          {/* Avatar con ícono de edición */}
          <div className="relative w-28 h-28 mx-auto mb-6 group">
            <img
              className="w-28 h-28 rounded-full border-4 border-violet-400 bg-gray-600 cursor-pointer object-cover transition-transform duration-200 group-hover:scale-105"
              src={
                selectedAvatar
                  ? getAvatarSrc(selectedAvatar)
                  : userData.avatar
                    ? getAvatarSrc(userData.avatar)
                    : getAvatarSrc(AVATAR_POR_DEFECTO)
              }
              alt="avatar"
              onError={handleAvatarError}
              onClick={() => setIsAvatarModalOpen(true)}
            />
            <div
              className="absolute bottom-1 right-1 w-8 h-8 bg-violet-600 rounded-full flex items-center justify-center cursor-pointer border-2 border-slate-900 transition-colors hover:bg-violet-500"
              onClick={() => setIsAvatarModalOpen(true)}
            >
              <FaPencilAlt className="w-3.5 h-3.5 text-white" />
            </div>
          </div>

          {/* Nick del usuario debajo del avatar */}
          <p className="text-center text-violet-300 font-semibold text-lg mb-5">
            {formData.nick_usuario}
          </p>

          {/* Mensajes de éxito/error */}
          {success && (
            <div className="bg-green-900/30 border border-green-500/40 rounded-lg px-4 py-2 mb-4 w-full max-w-3xl mx-auto">
              <p className="text-green-400 text-center text-sm">
                {t('cardPerfil.updateSuccess')}
              </p>
            </div>
          )}
          {error && (
            <div className="bg-red-900/30 border border-red-500/40 rounded-lg px-4 py-2 mb-4 w-full max-w-3xl mx-auto">
              <p className="text-red-400 text-center text-sm">{error}</p>
            </div>
          )}

          {/* Campos del perfil */}
          <div className="w-full max-w-3xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {PROFILE_FIELDS.map(({ key, label, displayValue }) => (
                <div
                  key={key}
                  className="flex items-center justify-between bg-slate-900/60 border border-slate-700 rounded-lg px-3 sm:px-4 py-3 gap-2"
                >
                  <div className="flex-1 min-w-0">
                    <span className="block text-[0.65rem] text-slate-400 uppercase tracking-wide mb-0.5">
                      {label}
                    </span>
                    <span className="block text-white text-xs sm:text-sm break-words">
                      {displayValue || formData[key]}
                    </span>
                  </div>
                  <div className="flex-shrink-0">
                    <BotonChico03
                      texto={t('cardPerfil.editButton')}
                      bgColor={key === 'fecha_nacimiento' ? 'gray' : 'blue'}
                      funcion={
                        key === 'fecha_nacimiento'
                          ? undefined
                          : () => handleEditClick(key)
                      }
                      disabled={key === 'fecha_nacimiento'}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Saldo como card destacada - ancho completo */}
            <div className="flex items-center justify-center bg-violet-900/20 border border-violet-500/30 rounded-lg px-4 py-4 mt-3">
              <div className="text-center">
                <span className="block text-xs text-violet-400 uppercase tracking-wide mb-0.5">
                  {t('cardPerfil.balanceLabel')}
                </span>
                <span className="block text-white text-xl font-bold">
                  ${userData.saldo}
                </span>
              </div>
            </div>

            {/* Reset saldo + contador */}
            <div className="flex items-center justify-between bg-slate-900/60 border border-slate-700 rounded-lg px-4 py-3 mt-3">
              <div className="flex-1 min-w-0">
                <span className="block text-[0.65rem] text-slate-400 uppercase tracking-wide mb-0.5">
                  {t('cardPerfil.resetsLabel')}
                </span>
                <span className="block text-white text-sm font-semibold">
                  {userData.reset ?? 0}
                </span>
              </div>
              <div className="flex-shrink-0">
                <BotonChico03
                  texto={resetting ? t('cardPerfil.resetting') : t('cardPerfil.resetButton')}
                  bgColor={resetting ? 'gray' : 'pink'}
                  funcion={
                    resetting ? undefined : () => setShowResetConfirm(true)
                  }
                  disabled={resetting}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de edición de campo */}
      {isEditing && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 p-4">
          <div className="bg-slate-900 border-2 border-slate-600 p-6 rounded-lg shadow-lg w-full max-w-md">
            <h3 className="text-xl font-bold mb-4 text-white">
              {t('cardPerfil.editButton')} {FIELD_LABELS[currentField] || currentField}
            </h3>
            <form onSubmit={handleEditSubmit}>
              {currentField === 'contrasenia' ? (
                <div className="space-y-3 mb-4">
                  <input
                    type="password"
                    name="contrasenia_actual"
                    value={passwordForm.contrasenia_actual}
                    onChange={(e) =>
                      setPasswordForm((prev) => ({
                        ...prev,
                        contrasenia_actual: e.target.value,
                      }))
                    }
                    placeholder={t('cardPerfil.currentPasswordPlaceholder')}
                    className="w-full px-4 py-2 text-sm text-white bg-slate-800 rounded-lg border-2 border-slate-600 focus:outline-none focus:border-violet-500"
                    autoFocus
                  />
                  <input
                    type="password"
                    name="nueva_contrasenia"
                    value={passwordForm.nueva_contrasenia}
                    onChange={(e) =>
                      setPasswordForm((prev) => ({
                        ...prev,
                        nueva_contrasenia: e.target.value,
                      }))
                    }
                    placeholder={t('cardPerfil.newPasswordPlaceholder')}
                    className="w-full px-4 py-2 text-sm text-white bg-slate-800 rounded-lg border-2 border-slate-600 focus:outline-none focus:border-violet-500"
                  />
                  <input
                    type="password"
                    name="confirmar_nueva_contrasenia"
                    value={passwordForm.confirmar_nueva_contrasenia}
                    onChange={(e) =>
                      setPasswordForm((prev) => ({
                        ...prev,
                        confirmar_nueva_contrasenia: e.target.value,
                      }))
                    }
                    placeholder={t('cardPerfil.repeatPasswordPlaceholder')}
                    className="w-full px-4 py-2 text-sm text-white bg-slate-800 rounded-lg border-2 border-slate-600 focus:outline-none focus:border-violet-500"
                  />
                </div>
              ) : (
                <input
                  type={FIELD_INPUT_TYPES[currentField] || 'text'}
                  name={currentField}
                  value={currentValue}
                  onChange={(e) => setCurrentValue(e.target.value)}
                  placeholder={t('cardPerfil.enterField', { field: FIELD_LABELS[currentField]?.toLowerCase() || '' })}
                  className="w-full px-4 py-2 text-sm text-white bg-slate-800 rounded-lg border-2 border-slate-600 focus:outline-none focus:border-violet-500 mb-4"
                  maxLength={FIELD_MAX_LENGTHS[currentField]}
                  autoFocus
                />
              )}
              {error && <p className="text-red-400 text-sm mb-3">{error}</p>}
              <div className="grid grid-cols-2 gap-2 [&_div]:w-full [&_button]:w-full">
                <BotonChico03
                  texto={t('cardPerfil.saveButton')}
                  bgColor={'green'}
                  funcion={handleEditSubmit}
                />
                <BotonChico03
                  texto={t('cardPerfil.cancelButton')}
                  bgColor={'red'}
                  funcion={handleEditCancel}
                />
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de selección de avatar */}
      {isAvatarModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 p-4">
          <div
            ref={avatarModalRef}
            className="bg-slate-900 border-2 border-slate-600 p-6 rounded-lg shadow-lg w-full max-w-3xl max-h-[85vh] flex flex-col"
          >
            <h3 className="text-xl text-white font-bold mb-4">
              {t('cardPerfil.selectAvatarTitle')}
            </h3>
            <div className="overflow-y-auto overflow-x-visible flex-1 custom-scrollbar">
              <div className="grid grid-cols-5 gap-2 justify-items-center p-2 overflow-visible">
                {AVATARES_DISPONIBLES.map((avatar) => {
                  const isLocked = lockedSet.has(avatar);
                  const lockInfo = LOCKED_AVATARS[avatar];
                  return (
                    <div
                      key={avatar}
                      className="relative"
                      onMouseEnter={() => isLocked && setTooltipAvatar(avatar)}
                      onMouseLeave={() => setTooltipAvatar(null)}
                    >
                      <img
                        src={getAvatarSrc(avatar)}
                        alt={`Avatar ${avatar}`}
                        onError={handleAvatarError}
                        className={`aspect-square w-20 sm:w-24 rounded-full object-cover transition-all duration-150 ${
                          isLocked
                            ? 'grayscale opacity-40 cursor-not-allowed'
                            : 'cursor-pointer hover:scale-105'
                        } ${
                          selectedAvatar === avatar && !isLocked
                            ? 'border-4 border-violet-400 shadow-[0_0_10px_rgba(167,139,250,0.6)]'
                            : 'border-2 border-transparent hover:border-slate-400'
                        }`}
                        onClick={() => !isLocked && setSelectedAvatar(avatar)}
                      />
                      {isLocked && (
                        <div
                          className={`absolute inset-0 flex items-center justify-center pointer-events-none transition-opacity duration-200 ${tooltipAvatar === avatar ? 'opacity-0' : 'opacity-100'}`}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-8 h-8 text-slate-300 drop-shadow-lg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M12 1.5a5.25 5.25 0 00-5.25 5.25v3a3 3 0 00-3 3v6.75a3 3 0 003 3h10.5a3 3 0 003-3v-6.75a3 3 0 00-3-3v-3A5.25 5.25 0 0012 1.5zm3.75 8.25v-3a3.75 3.75 0 10-7.5 0v3h7.5z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </div>
                      )}
                      {tooltipAvatar === avatar && isLocked && lockInfo && (
                        <div className="absolute bottom-full left-0 mb-2 w-52 bg-slate-800 border border-violet-500/50 text-violet-200 text-xs rounded-lg px-4 py-2.5 shadow-lg z-50 text-center pointer-events-none whitespace-normal">
                          {t('cardPerfil.lockedAvatarDescription')}
                          <div className="absolute top-full left-6 border-4 border-transparent border-t-slate-800"></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            {error && <p className="text-red-400 text-sm mt-3">{error}</p>}
            <div className="flex justify-end gap-2 mt-4 pt-3 border-t border-slate-700">
              <BotonChico03
                texto={t('cardPerfil.saveButton')}
                bgColor={'green'}
                funcion={handleAvatarUpdate}
              />
              <BotonChico03
                texto={t('cardPerfil.cancelButton')}
                bgColor={'red'}
                funcion={handleAvatarCancel}
              />
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de reset */}
      {showResetConfirm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-60 z-50 p-4">
          <div className="bg-slate-900 border-2 border-slate-600 p-6 rounded-lg shadow-lg w-full max-w-sm">
            <h3 className="text-xl font-bold mb-3 text-white text-center">
              {t('cardPerfil.resetConfirmTitle')}
            </h3>
            <p className="text-gray-300 text-sm mb-2 text-center">
              {t('cardPerfil.resetMessage1')}{' '}
              <span className="text-emerald-400 font-bold">{t('cardPerfil.resetAmount')}</span>{' '}
              {t('cardPerfil.resetMessage2')}{' '}
              <span className="text-orange-400 font-bold">{t('cardPerfil.resetCounter')}</span>{' '}
              {t('cardPerfil.resetMessage3')}
            </p>
            <p className="text-gray-400 text-xs mb-5 text-center">
              {t('cardPerfil.resetConfirmation')}
            </p>
            <div className="grid grid-cols-2 gap-2 [&_div]:w-full [&_button]:w-full">
              <BotonChico03
                texto={t('cardPerfil.confirmButton')}
                bgColor={'green'}
                funcion={handleResetSaldo}
              />
              <BotonChico03
                texto={t('cardPerfil.cancelButton')}
                bgColor={'red'}
                funcion={() => setShowResetConfirm(false)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CardPerfil;
